<template>
  <header v-if="showHeader" class="admin-page-header" role="banner">
    <div class="admin-page-header__top">
      <el-button
        v-if="showSubnavToggle"
        class="subnav-toggle"
        size="small"
        :icon="Menu"
        @click="emit('open-subnav')"
      >
        {{ t('subNav') }}
      </el-button>
      <nav v-if="hasBreadcrumbs" class="admin-breadcrumb" aria-label="面包屑导航">
        <el-breadcrumb separator="/">
          <el-breadcrumb-item
            v-for="(crumb, index) in breadcrumbs"
            :key="`${crumb.path}-${index}`"
          >
            <router-link
              v-if="index < breadcrumbs.length - 1"
              :to="crumb.path"
              class="admin-breadcrumb__link"
            >
              {{ crumb.title }}
            </router-link>
            <span v-else class="admin-breadcrumb__current" aria-current="page">
              {{ crumb.title }}
            </span>
          </el-breadcrumb-item>
        </el-breadcrumb>
      </nav>
      <h1 v-else-if="displayTitle" class="admin-page-header__title">{{ displayTitle }}</h1>
      <div v-if="$slots.extra" class="admin-page-header__extra">
        <slot name="extra" />
      </div>
    </div>
    <p v-if="displayDesc" class="admin-page-header__desc">{{ displayDesc }}</p>
  </header>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { Menu } from '@element-plus/icons-vue'
import { getPageTitleByPath, getBreadcrumbsByPath } from '@/utils/pageTitle.js'
import { t } from '@/locale/index.js'

const props = defineProps({
  title: { type: String, default: '' },
  description: { type: String, default: '' },
  hideBreadcrumb: { type: Boolean, default: false },
  showSubnavToggle: { type: Boolean, default: false },
})

const emit = defineEmits(['open-subnav'])

const route = useRoute()

const displayTitle = computed(() => props.title || getPageTitleByPath(route.path))

const displayDesc = computed(() => {
  if (props.description) return props.description
  if (route.path === '/home') return t('detail.workbenchDesc')
  return ''
})

const breadcrumbs = computed(() => {
  if (props.hideBreadcrumb) return []
  return getBreadcrumbsByPath(route.path)
})

const hasBreadcrumbs = computed(() => breadcrumbs.value.length > 0)

const showHeader = computed(() => Boolean(hasBreadcrumbs.value || displayTitle.value))
</script>

<style lang="less" scoped>
.admin-page-header {
  flex-shrink: 0;
  margin-bottom: var(--admin-gap-md, 12px);
}

.admin-page-header__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 28px;
}

.admin-breadcrumb {
  flex: 1;
  min-width: 0;

  :deep(.el-breadcrumb__inner) {
    font-size: 18px;
    line-height: 1.35;
  }

  :deep(.el-breadcrumb__separator) {
    font-size: 16px;
    color: var(--admin-text-secondary, #909399);
  }
}

.admin-breadcrumb__link {
  color: var(--admin-text-secondary, #909399);
  text-decoration: none;
  transition: color 0.15s;

  &:hover {
    color: var(--admin-primary, #409eff);
  }
}

.admin-breadcrumb__current {
  color: var(--admin-text-primary, #1a1a1a);
  font-weight: 600;
}

.admin-page-header__title {
  margin: 0;
  flex: 1;
  min-width: 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--admin-text-primary, #1a1a1a);
  line-height: 1.35;
  letter-spacing: -0.01em;
}

.subnav-toggle {
  flex-shrink: 0;
}

.admin-page-header__extra {
  flex-shrink: 0;
}

.admin-page-header__desc {
  margin: 6px 0 0;
  font-size: 13px;
  color: var(--admin-text-secondary, #909399);
  line-height: 1.5;
}
</style>
